import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import cx from 'clsx';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, useQuery, iconBtnStyle } from '../../../../packages';
import Notes from '../../sessions/video-call-screen/components/notes';
import Schedules from '../../sessions/video-call-screen/components/schedules';
import { startOrJoinCall } from '../../../../services/appointment/appointment.service';
import { OPENTOK_APIKEY } from '../../../../constants/CommonConstants';
import history from '../../../../utils/history';
import { getMessages } from '../../../../redux/modules/conversation/selectors';
import { sortingDesToAsc } from '../../../../utils/CommonUtils';
import PriorityList from '../../sessions/video-call-screen/components/priority-list';
import { showSnackbar } from '../../../../redux/modules/snackbar';
import { useStyles } from './VideoChats.styles';
import TopHeader from './top-header';
import TopBar from './top-bar';
import { tabsLeft } from './top-header/TopHeader';
import Chats from './chats';
import Members from './members';

const SubscriberVideo = ({ children, width, height, pinned, thumbNail, onPin }) => {
  const classes = useStyles();
  const subscriberContainerRef = useRef(null);

  const onPinHandler = () => {
    if (pinned) {
      setPinnedIndex(null);
    } else {
      onPin();
    }
  };

  // Forward the ref container to parent function child for DOM mounting
  useEffect(() => {
    if (subscriberContainerRef.current && typeof children === 'function') {
      children(subscriberContainerRef.current);
    }
  }, [children]);

  const innerWidth = pinned ? 'calc(90% - 30px)' : thumbNail ? '10%' : width;
  const innerHeight = pinned ? '100%' : thumbNail ? 'calc(25% - 22.5px)' : height;

  return (
    <Box
      sx={{
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'all ease-in-out 0.4s',
        position: 'relative',
        '& > div': {
          width: '100%',
          height: '100%',
          '& > div': {
            width: '100% !important',
            height: '100% !important',
          },
        },
        width: innerWidth,
        height: innerHeight,
        order: pinned ? -1 : 0,
        '&:hover': {
          '& > .pin': {
            display: 'block',
          },
        },
      }}
    >
      <div ref={subscriberContainerRef} style={{ width: '100%', height: '100%' }} />
      <IconButton
        icon={pinned ? 'unpin' : 'pin'}
        onClick={onPinHandler}
        className={`pin ${classes.pin}`}
        style={iconBtnStyle.HIGH_CONTRAST}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
//  Permissions helpers – copied from the working member implementation
// ---------------------------------------------------------------------------

const checkMediaPermissions = async () => {
  try {
    const cameraStatus = await navigator.permissions.query({ name: 'camera' });
    const micStatus = await navigator.permissions.query({ name: 'microphone' });

    if (cameraStatus.state !== 'granted' || micStatus.state !== 'granted') {
      alert('Please enable both camera and microphone permissions.');
      return false;
    }

    try {
      const test = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      test.getTracks().forEach(t => t.stop());
      return true;
    } catch (err) {
      alert('Unable to access camera and/or microphone.');
      return false;
    }
  } catch (err) {
    alert('Error while checking media permissions.');
    return false;
  }
};

const VideoChats = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { channelUrl } = useParams();
  const params = useQuery();

  const [showShareScreenSelection, setShowShareScreenSelection] = useState(false);
  const [selectedSession, setSelectedSession] = useState(tabsLeft[0].type);
  const [notes, setNotes] = useState('');

  const [call, setCall] = useState({});
  const [publishVideo, setPublishVideo] = useState(true);
  const [publishAudio, setPublishAudio] = useState(true);
  const [numberOfParticipants, setNumberOfSubscribers] = useState(0);
  const messages = useSelector(getMessages);
  const sortedMessage = messages.sort(sortingDesToAsc);
  const [fullScreen, setFullScreen] = useState(false);
  const [participant, setParticipant] = useState({});
  const [mediaPermissionsGranted, setMediaPermissionsGranted] = useState(false);
  // Which subscriber index is currently pinned (null = none)
  const [pinnedIndex, setPinnedIndex] = useState(null);

  // Refs for OpenTok.js
  const sessionRef = useRef(null);
  const publisherRef = useRef(null);
  const screenPublisherRef = useRef(null);
  const publisherContainerRef = useRef(null);
  const subscribersRef = useRef([]);
  const subscriberContainersRef = useRef([]);

  const isDirectCall = Boolean(params.get('isDirectCall'));

  const [participantScreenShared, setParticipantScreenShared] = useState(false);
  const [screenShareStreamId, setScreenShareStreamId] = useState(null);
  const screenShareContainerRef = useRef(null);

  const getColWidth = () => {
    if (numberOfParticipants === 1) {
      return '100%';
    }
    if (numberOfParticipants <= 4) {
      return 'calc(50% - 15px)';
    }
    if (numberOfParticipants <= 6) {
      return `calc(${100 / 3}% - 20px)`;
    }
    if (numberOfParticipants <= 8) {
      return `calc(25% - 22.5px)`;
    }
    return `calc(${100 / 6}% - 30px)`;
  };

  const getAvatarHeight = () => {
    if (numberOfParticipants <= 2) {
      return '100%';
    }
    if (numberOfParticipants <= 12) {
      return 'calc(50% - 15px)';
    }
    return `calc(${100 / 3}% - 20px)`;
  };

  const topHeaderTabChange = type => {
    setSelectedSession(type);
  };

  const onCloseSession = () => {
    setSelectedSession(tabsLeft[0].type);
  };

  const onStartOrJoinCall = async () => {
    try {
      const res = await startOrJoinCall(
        {
          channelUrl,
        },
        { isDirectCall },
      );
      setCall(res.data);

      // Initialize the session
      sessionRef.current = OT.initSession(OPENTOK_APIKEY, res?.data?.sessionId);

      // Set up session event handlers
      sessionRef.current.on('streamCreated', event => {
        // -----------------------------------------------------------------
        //  1) SCREEN SHARE HANDLING
        // -----------------------------------------------------------------
        if (event.stream.videoType === 'screen') {
          // Ignore our own screen share – we already have a local preview.
          const isOwnStream =
            sessionRef.current?.connection?.connectionId === event.stream?.connection?.connectionId;

          if (!isOwnStream) {
            setParticipantScreenShared(true);
            setScreenShareStreamId(event.stream.streamId);

            const screenSubscriberOptions = {
              insertMode: 'append',
              width: '100%',
              height: '100%',
              fitMode: 'contain',
              subscribeToAudio: false,
              subscribeToVideo: true,
              style: { buttonDisplayMode: 'off' },
            };

            sessionRef.current.subscribe(
              event.stream,
              document.getElementById('memberScreenContainer'),
              screenSubscriberOptions,
              error => {
                if (error) {
                  console.log('Error subscribing to participant screen share:', error);
                } else {
                  console.log('Successfully subscribed to participant screen share');
                }
              },
            );
          }
          return; // Do not execute regular video logic for screen streams
        }

        // -----------------------------------------------------------------
        //  2) REGULAR CAMERA VIDEO STREAMS
        // -----------------------------------------------------------------
        // Create a container for the subscriber
        const subscriberContainer = document.createElement('div');
        subscriberContainer.style.width = '100%';
        subscriberContainer.style.height = '100%';

        // Store the container reference
        subscriberContainersRef.current.push(subscriberContainer);

        // Create subscriber
        const subscriberOptions = {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          fitMode: 'contain',
        };

        const subscriber = sessionRef.current.subscribe(
          event.stream,
          subscriberContainer,
          subscriberOptions,
          error => {
            if (error) {
              console.log('There was an error subscribing');
            } else {
              console.log('Subscriber connected to the stream');
              setNumberOfSubscribers(prevCount => prevCount + 1);

              // Set up subscriber event handlers
              subscriber.on('disconnected', () => {
                console.log('Subscriber disconnected from the stream');
                setNumberOfSubscribers(prevCount => (prevCount > 0 ? prevCount - 1 : 0));
              });

              subscriber.on('destroyed', () => {
                console.log('Subscriber destroyed');
                setNumberOfSubscribers(prevCount => (prevCount > 0 ? prevCount - 1 : 0));
              });
            }
          },
        );

        // Store the subscriber reference
        subscribersRef.current.push(subscriber);

        // Auto-pin first subscriber when it is the only stream
        if (subscribersRef.current.length === 1) {
          setPinnedIndex(0);
        }
      });

      sessionRef.current.on('signal', event => {
        if (event.type === 'session-ended' || event.type === 'signal:session-ended') {
          console.log('Session ended signal received');
        }
      });

      sessionRef.current.on('streamDestroyed', event => {
        console.log('Stream destroyed');

        // Check if this is a screen share stream
        if (event.stream.videoType === 'screen' || event.stream.streamId === screenShareStreamId) {
          console.log('Participant screen sharing stopped');
          setParticipantScreenShared(false);
          setScreenShareStreamId(null);
        } else {
          // Handle regular video stream destruction
          // Find and remove the subscriber and container
          const streamId = event.stream.streamId;
          const index = subscribersRef.current.findIndex(sub => sub.stream.streamId === streamId);

          if (index !== -1) {
            sessionRef.current.unsubscribe(subscribersRef.current[index]);
            subscribersRef.current.splice(index, 1);
            subscriberContainersRef.current.splice(index, 1);
            setNumberOfSubscribers(prevCount => (prevCount > 0 ? prevCount - 1 : 0));

            // Adjust pinned index if necessary
            if (pinnedIndex !== null) {
              if (subscribersRef.current.length === 0) {
                setPinnedIndex(null);
              } else if (pinnedIndex >= subscribersRef.current.length) {
                setPinnedIndex(0);
              }
            }
          }
        }
      });

      sessionRef.current.on('connectionDestroyed', event => {
        console.log('Connection destroyed');
        setNumberOfSubscribers(prevCount => (prevCount > 0 ? prevCount - 1 : 0));
      });

      sessionRef.current.on('sessionConnected', event => {
        console.log('Session connected');
      });

      sessionRef.current.on('sessionDisconnected', event => {
        console.log('Session disconnected');
      });

      // Connect to the session
      sessionRef.current.connect(res?.data?.sessionToken, error => {
        if (error) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Failed to connect to the session. Please try again.',
            }),
          );
        } else {
          // Initialize publisher
          const publisherOptions = {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            publishAudio,
            publishVideo,
          };

          publisherRef.current = OT.initPublisher(publisherContainerRef.current, publisherOptions, error => {
            if (error) {
              console.log('There was an error initializing the publisher');
            } else {
              // Publish to the session
              sessionRef.current.publish(publisherRef.current, error => {
                if (error) {
                  console.log('Error publishing');
                } else {
                  console.log('Publisher initialized and publishing');
                }
              });
            }
          });

          // Set up publisher event handlers
          publisherRef.current.on('streamCreated', event => {
            console.log('Publisher stream created');
          });

          publisherRef.current.on('streamDestroyed', event => {
            console.log('Publisher stream destroyed');
          });

          publisherRef.current.on('mediaStopped', () => {
            if (showShareScreenSelection) {
              onStopSharingClick();
            }
          });
        }
      });
    } catch (error) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage:
            error?.errors?.[0]?.endUserMessage ?? 'Unable to initiate call, please try again later',
        }),
      );
      history.back();
    }
  };

  const onShareScreenClick = () => {
    setShowShareScreenSelection(true);

    if (sessionRef.current && !screenPublisherRef.current) {
      OT.checkScreenSharingCapability(response => {
        if (!response.supported || response.extensionRegistered === false) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Screen sharing is not supported in your browser.',
            }),
          );
          setShowShareScreenSelection(false);
          return;
        }

        const screenOptions = {
          videoSource: 'screen',
          width: 200,
          height: 114,
          insertMode: 'append',
        };

        // Defer initialisation until the TopBar re-renders the preview container.
        setTimeout(() => {
          const container = document.getElementById('screenShareContainer');
          if (!container) {
            console.error('screenShareContainer not found – aborting share');
            setShowShareScreenSelection(false);
            return;
          }

          screenPublisherRef.current = OT.initPublisher(container, screenOptions, error => {
            if (error) {
              console.log('Error initializing screen publisher');
              setShowShareScreenSelection(false);
            } else {
              // Publish screen to the session
              sessionRef.current.publish(screenPublisherRef.current, error => {
                if (error) {
                  console.log('Error publishing screen');
                  setShowShareScreenSelection(false);
                } else {
                  console.log('Screen publisher initialized and publishing');
                }
              });
            }
          });

          // Screen-share lifecycle handlers
          screenPublisherRef.current.on('streamDestroyed', () => {
            console.log('Screen sharing stopped');
            setShowShareScreenSelection(false);
            screenPublisherRef.current = null;
          });

          screenPublisherRef.current.on('mediaStopped', () => {
            console.log('Screen sharing media stopped');
            setShowShareScreenSelection(false);
            if (screenPublisherRef.current) {
              sessionRef.current.unpublish(screenPublisherRef.current);
              screenPublisherRef.current = null;
            }
          });
        }, 0);
      });
    }
  };

  const onStopSharingClick = () => {
    if (showShareScreenSelection && screenPublisherRef.current && sessionRef.current) {
      sessionRef.current.unpublish(screenPublisherRef.current);
      screenPublisherRef.current = null;
      setShowShareScreenSelection(false);
    }
  };

  const endSession = () => {
    if (sessionRef.current) {
      // Send session-ended signal to other participants
      sessionRef.current.signal(
        {
          type: 'session-ended',
          data: 'The session has ended',
        },
        error => {
          if (error) {
            console.log('Error sending signal');
          } else {
            console.log('Signal sent successfully');
          }
        },
      );

      // Clean up publishers
      if (screenPublisherRef.current) {
        sessionRef.current.unpublish(screenPublisherRef.current);
        screenPublisherRef.current = null;
      }

      if (publisherRef.current) {
        sessionRef.current.unpublish(publisherRef.current);
        publisherRef.current = null;
      }

      // Clean up subscribers
      subscribersRef.current.forEach(subscriber => {
        if (subscriber) {
          sessionRef.current.unsubscribe(subscriber);
        }
      });
      subscribersRef.current = [];
      subscriberContainersRef.current = [];

      // Disconnect from the session
      sessionRef.current.disconnect();
      sessionRef.current = null;

      history.back();
    }
  };

  useEffect(() => {
    if (!channelUrl) return;

    // Only attempt to start/join once permissions are confirmed.
    if (mediaPermissionsGranted) {
      void onStartOrJoinCall();
    }

    // Cleanup function
    return () => {
      if (sessionRef.current) {
        if (screenPublisherRef.current) {
          sessionRef.current.unpublish(screenPublisherRef.current);
          screenPublisherRef.current = null;
        }

        if (publisherRef.current) {
          sessionRef.current.unpublish(publisherRef.current);
          publisherRef.current = null;
        }

        subscribersRef.current.forEach(subscriber => {
          if (subscriber) {
            sessionRef.current.unsubscribe(subscriber);
          }
        });

        sessionRef.current.disconnect();
        sessionRef.current = null;
      }
    };
  }, [channelUrl, mediaPermissionsGranted]);

  // Run permission check once on mount
  useEffect(() => {
    (async () => {
      const granted = await checkMediaPermissions();
      setMediaPermissionsGranted(granted);
    })();
  }, []);

  // Update publisher video state
  useEffect(() => {
    if (publisherRef.current) {
      publisherRef.current.publishVideo(publishVideo);
    }
  }, [publishVideo]);

  // Update publisher audio state
  useEffect(() => {
    if (publisherRef.current) {
      publisherRef.current.publishAudio(publishAudio);
    }
  }, [publishAudio]);

  const toggleFullScreen = () => {
    setFullScreen(on => !on);
  };

  return (
    <Box className={classes.mainContainer}>
      {!fullScreen && (
        <TopHeader
          selectedSession={selectedSession}
          onChange={topHeaderTabChange}
          numberOfParticipants={numberOfParticipants + 1}
          onEndSession={endSession}
        />
      )}
      {!call.sessionId || !mediaPermissionsGranted ? (
        <div className={classes.video} />
      ) : (
        <Box height={fullScreen ? '100vh !important' : 'calc(100vh - 80px)'} className={classes.content}>
          <Box className={classes.container}>
            <Box className={cx(classes.rightPanel)} />
            <Box className={classes.videoContainer}>
              <TopBar
                onShareScreenClick={onShareScreenClick}
                toggleMic={setPublishAudio}
                toggleCamera={setPublishVideo}
                onEndSession={endSession}
                toggleFullScreen={toggleFullScreen}
                onStopSharingClick={onStopSharingClick}
                publisher={<div ref={publisherContainerRef} style={{ width: 200, height: 114 }} />}
                screen={
                  showShareScreenSelection && (
                    <div id="screenShareContainer" style={{ width: 200, height: 114 }} />
                  )
                }
              />
              <Box className={cx(classes.video)}>
                <Box paddingTop="160px" height="100%" position="relative">
                  {/* Screen Share Container (positioned absolutely to cover the video area) */}
                  <div
                    id="memberScreenContainer"
                    ref={screenShareContainerRef}
                    className={participantScreenShared ? classes.screenShareVisible : classes.streamHidden}
                  />

                  <Box
                    className={cx(classes.subscriberVideo, {
                      [classes.subscriberVideoMini]: participantScreenShared,
                    })}
                    sx={{
                      '& > div': {
                        display: 'flex',
                        gap: '30px',
                        width: '100%',
                        height: '100%',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: pinnedIndex !== null ? 'column' : 'row',
                        overflow: 'hidden',
                        position: participantScreenShared ? 'relative' : 'static',
                        zIndex: participantScreenShared ? 15 : 'auto',
                      },
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '30px',
                        flexWrap: 'wrap',
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      {/* Render subscriber containers */}
                      {Array.from({ length: numberOfParticipants }).map((_, index) => (
                        <SubscriberVideo
                          key={index}
                          width={getColWidth()}
                          height={getAvatarHeight()}
                          pinned={pinnedIndex === index}
                          thumbNail={
                            (pinnedIndex !== null && pinnedIndex !== index) || participantScreenShared
                          }
                          onPin={() => setPinnedIndex(index)}
                        >
                          {containerRef => {
                            if (containerRef && subscriberContainersRef.current[index]) {
                              // Move the existing subscriber container to this new container
                              containerRef.appendChild(subscriberContainersRef.current[index]);
                            }
                            return null;
                          }}
                        </SubscriberVideo>
                      ))}
                    </div>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={cx(classes.rightPanel, {
                [classes.chatPanel]:
                  selectedSession === tabsLeft[1].type ||
                  (selectedSession === tabsLeft[4].type && !participant?.participantId),
                [classes.notePanel]: selectedSession === tabsLeft[2].type,
                [classes.schedulePanel]: selectedSession === tabsLeft[3].type,
                [classes.priorityPanel]: selectedSession === tabsLeft[4].type && participant?.participantId,
              })}
            >
              {selectedSession === tabsLeft[2].type && (
                <Notes onClose={onCloseSession} notes={notes} setNotes={setNotes} />
              )}
              {selectedSession === tabsLeft[3].type && <Schedules onClose={onCloseSession} />}
              {selectedSession === tabsLeft[1].type && (
                <Chats onClose={onCloseSession} messages={sortedMessage} channelUrl={channelUrl} />
              )}
              {selectedSession === tabsLeft[4].type && !participant?.participantId && (
                <Members onSelect={setParticipant} onClose={onCloseSession} />
              )}
              {selectedSession === tabsLeft[4].type && participant?.participantId && (
                <PriorityList appointment={participant} hasBack onClose={() => setParticipant({})} />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export { VideoChats };
